import React, { useEffect, useState } from 'react';
import { PencilSquare, Trash } from 'react-bootstrap-icons';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import './ProfilePage.css';
import {toast} from 'react-hot-toast'

const API = process.env.REACT_APP_API_URL;

const ProfilePage = ({ user, setUser }) => {
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [addresses, setAddresses] = useState([]);
  const [newAddress, setNewAddress] = useState({
    name: '',
    phone_number: '',
    type: 'Home',           // ← default value
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: ''
  });
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        // Fetch user profile
        const userRes = await fetch(`${API}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (userRes.ok) {
          const userData = await userRes.json();
          setUser(userData);
        }

        // Fetch addresses
        const addrRes = await fetch(`${API}/api/user/addresses`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (addrRes.ok) {
          const addrData = await addrRes.json();
          setAddresses(Array.isArray(addrData) ? addrData : []);
        }
      } catch (err) {
        console.error('Profile/addresses fetch failed:', err);
      }
    };

    if (token) fetchProfile();
  }, [token, setUser]);

  const handleEdit = (field, value) => {
    setEditingField(field);
    setTempValue(value || '');
  };

  const handleSave = async () => {
    try {
      const updatedUser = { ...user, [editingField]: tempValue.trim() };
      setUser(updatedUser);
      setEditingField(null);

      await fetch(`${API}/api/user/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(updatedUser)
      });
    } catch (err) {
      console.error('Profile update failed:', err);
    }
  };

  const handleAddAddress = async () => {
    if (!newAddress.name.trim() || !newAddress.phone_number || !newAddress.line1.trim() || !newAddress.city.trim() || !newAddress.type) {
      toast.error('Please fill all required fields: Name, Phone, Type, Line 1, City');
      return;
    }

    try {
      const res = await fetch(`${API}/api/user/addresses/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newAddress)
      });

      if (res.ok) {
  toast.success("Address added");

  const addrRes = await fetch(`${API}/api/user/addresses`, {
    headers: { Authorization: `Bearer ${token}` }
  });

  if (addrRes.ok) {
    const addrData = await addrRes.json();
    setAddresses(addrData);
  }

  setIsAddingAddress(false);
} else {
        const errorText = await res.text();
        console.error('Add address failed:', errorText);
        toast.error(`Failed to add address: ${errorText}`);
      }
    } catch (err) {
      console.error('Add address error:', err);
      toast.error('Something went wrong while adding the address');
    }
  };

  const handleDeleteAddress = async () => {
  if (!selectedAddressId) return;

  try {
    const res = await fetch(`${API}/api/user/addresses/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({ id: selectedAddressId })
    });

    if (res.ok) {
      setAddresses(addresses.filter((a) => a.id !== selectedAddressId));
      setShowDeleteModal(false);
      setSelectedAddressId(null);
      toast.success("Address deleted");
    }
  } catch (err) {
    console.error(err);
  }
};

  return (
    <div className="profile-wrapper">
      <div className="profile-container">

        <div className="header-section">
          <h1>My Profile</h1>
          <p>Manage your personal information and addresses</p>
        </div>

        {/* ── Personal Details ── */}
        <div className="card-section">
          <div className="section-title">Personal Details</div>

          {['name', 'email', 'mobile'].map((field) => (
            <div key={field} className="detail-row">
              <label>{field.toUpperCase()}</label>

              {editingField === field ? (
                <div className="input-group">
                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                  />
                  <button className="btn btn-primary btn-sm" onClick={handleSave}>
                    Save
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingField(null)}>
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="display-group">
                  <span className="value-text">{user?.[field] || '—'}</span>
                  <button className="icon-btn" onClick={() => handleEdit(field, user?.[field])}>
                    <PencilSquare />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ── Addresses ── */}
        <div className="card-section">
          <div className="section-header-row">
            <div className="section-title">My Addresses</div>
            {!isAddingAddress && (
              <button className="btn btn-primary btn-sm" onClick={() => setIsAddingAddress(true)}>
                Add Address
              </button>
            )}
          </div>

          <div className="address-list">
            {addresses.map((addr) => (
              <div key={addr.id} className="address-cards">
                <h5>{addr.name}</h5>
                <p>{addr.type}</p>
                <p>{addr.phone_number}</p>
                <p>{addr.line1}</p>

                {addr.line2 && <p>{addr.line2}</p>}
                <p className="city-zip">
                  {addr.city}, {addr.state} — {addr.zip}
                </p>
                

                <div className="address-actions">
                  <button
  className="delete-btn"
  onClick={() => {
    setSelectedAddressId(addr.id);
    setShowDeleteModal(true);
  }}
>
                    <Trash size={18} />
                  </button>
                </div>
              </div>
            ))}

            {addresses.length === 0 && !isAddingAddress && (
              <p className="no-data text-black text-lg">No addresses added yet.</p>
            )}
          </div>

          {/* Add Address Form */}
          {isAddingAddress && (
            <div className="new-address-form" autoComplete="off">
              <div className="form-grid">
                <div className="form-group">
                  <label>NAME *</label>
                  <input
                    value={newAddress.name}
                    onChange={(e) => setNewAddress({ ...newAddress, name: e.target.value })}
                    placeholder="Full name"
                  />
                </div>

                <div className="form-group">
                  <label>PHONE NUMBER *</label>
                  <PhoneInput
                    international
                    defaultCountry="IN"
                    value={newAddress.phone_number}
                    onChange={(value) => setNewAddress({ ...newAddress, phone_number: value || '' })}
                    placeholder="Enter phone number"
                    className="phone-input-custom"
                  />
                </div>

                <div className="form-group">
                  <label>ADDRESS TYPE *</label>
                  <select
                    value={newAddress.type}
                    onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
                  >
                    <option value="Home">Home</option>
                    <option value="Work">Work</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>LINE 1 *</label>
                  <input
                    value={newAddress.line1}
                    onChange={(e) => setNewAddress({ ...newAddress, line1: e.target.value })}
                    placeholder="Street address, flat no, etc."
                  />
                </div>

                <div className="form-group">
                  <label>LINE 2</label>
                  <input
                    value={newAddress.line2}
                    onChange={(e) => setNewAddress({ ...newAddress, line2: e.target.value })}
                    placeholder="Apartment, suite, landmark (optional)"
                  />
                </div>

                <div className="form-group">
                  <label>CITY *</label>
                  <input
                    value={newAddress.city}
                    onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>STATE / PROVINCE</label>
                  <input
                    value={newAddress.state}
                    onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>ZIP / PIN CODE</label>
                  <input
                    value={newAddress.zip}
                    onChange={(e) => setNewAddress({ ...newAddress, zip: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button className="btn btn-primary" onClick={handleAddAddress}>
                  Save Address
                </button>
                <button className="btn btn-secondary" onClick={() => setIsAddingAddress(false)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showDeleteModal && (
  <div className="modal-overlay">
    <div className="custom-modal">
      <h4>Delete Address</h4>
      <p>Are you sure you want to delete this address?</p>

      <div className="modal-actions">
        <button
          className="btn btn-danger"
          onClick={handleDeleteAddress}
        >
          Delete
        </button>

        <button
          className="btn btn-secondary"
          onClick={() => {
            setShowDeleteModal(false);
            setSelectedAddressId(null);
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default ProfilePage;